import { GAME_LAYOUT, Settings } from "./settings";
import {
  WILD_SYMBOL_ID,
  SCATTER_SYMBOL_IDS,
  BONUS_SYMBOL_IDS,
} from "./SymbolData";

// The active provider's config, keyed by Settings.APP.RGS_PROVIDER — see RGS/activeProviderConfig.
import { PROVIDER_CONFIG } from "./RGS/activeProviderConfig";

// ============================================================================
// REEL DELAY GENERATORS — dynamically create per-reel delay arrays from REEL_COUNT
// ============================================================================

/**
 * Generate a delay array with constant gaps between reels.
 * e.g. generateLinearDelays(3, 375) → [0, 375, 750]
 */
const generateLinearDelays = (count, gap) =>
  Array.from({ length: count }, (_, i) => i * gap);

/**
 * Generate a delay array with gaps that decrease per reel (natural cascade feel).
 * Negative gapReduction means gaps INCREASE per reel.
 * e.g. generateDecreasingDelays(5, 50, 7, 25) → [0, 50, 93, 129, 158]
 */
const generateDecreasingDelays = (
  count,
  initialGap,
  gapReduction = 0,
  minGap = 0,
) => {
  const delays = [0];
  let gap = initialGap;
  for (let i = 1; i < count; i++) {
    delays.push(delays[i - 1] + gap);
    gap = Math.max(gap - gapReduction, minGap);
  }
  return delays;
};

// Feature configuration
/**
 * Loops a win animation should play.
 *
 * A FAST win substitutes WIN_FAST.LOOPS for whatever the normal count would be,
 * because `win_fast` is about half the length of `win` and one loop of it does
 * not read. Falls back to the normal count when WIN_FAST.LOOPS is unset, so
 * removing the knob restores the old behaviour exactly.
 *
 * @param {boolean} useFastWin
 * @param {number}  normalLoops  LOOPS_PER_LINE, or LOOPS_FOR_SHOW_ALL.
 */
export const winLoops = (useFastWin, normalLoops) => {
  const fallback = normalLoops || 1;
  if (!useFastWin) return fallback;
  return FEATURES_CONFIG.WIN_ANIMATION_TIMING.WIN_FAST?.LOOPS || fallback;
};

export const FEATURES_CONFIG = {
  DEV_TOOLS: {
    DEBUG_BORDER: false, // If true: shows 1px yellow debug border on key components (DimReelSet, etc.) for measuring alignment
    DEBUG_CROSSHAIR: false, // If true: draws 1px red crosshair lines across the full screen at the vertical and horizontal center
    // DEV_PANEL: true, // ON for sizing the H&W intro / reload (2026-09-30).
    // DEV_PANEL: false, // off again — sizing done (2026-09-30).
    // DEV_PANEL: true, // ON for the FS Max Win / Better Luck popups (2026-09-30).
    DEV_PANEL: false, // off for the commit (2026-09-30). If true: renders a dev overlay panel in the top-left corner with test trigger buttons (shake, transition, etc.)
    // If true: the Cleopatra Hold & Win room (HoldAndWinGameView) is drawn over
    // the base room, so its layout can be checked before the feature exists.
    // Temporary — the H&W active flag replaces it.
    PREVIEW_HOLD_AND_WIN_VIEW: false, // true = the room over everything, with a random spin loop (layout checks only)
    // If true: every gate that can refuse a free spin logs the reason (see
    // utils/fsDiag). Turn on when a free-spins round stops on its own — each
    // gate is silent by design, so without this they are indistinguishable.
    // FS_DIAGNOSTICS: true,
    FS_DIAGNOSTICS: false, // off for the commit (2026-09-30)
    // If true: one `[$]` line for every action that moves a number the player
    // reads — balance, displayed balance, the free-spins and free-rounds
    // totals, the win-display flags — naming the action and the before -> after
    // (see utils/moneyTrace). Plus one line whenever the win display changes
    // which rows it shows. For balance / Total Win bugs. Turn off before
    // shipping.
    MONEY_DIAGNOSTICS: false,
    // If true: one console line per second with frame time, draw calls and —
    // the point of it — how many spine skeletons are being SOLVED every frame
    // while nobody can see them. See utils/perfProbe. Turn off before shipping.
    PERF_PROBE: false,
    // TEMPORARY. If true: every win-celebration sound logs the numbers that
    // decide how loud it actually is — configured volume, the Howl's real
    // volume, mute state, and the global Howler volume. Turn off when the mix
    // is settled.
    SOUND_DIAGNOSTICS: false,
    // TEMPORARY. If true: the free-spins loop never fires a spin, so the round
    // enters and STOPS on the board it opened with. For inspecting the arrival
    // state — the settled gamble scatter, the room, the boxes — which is
    // otherwise on screen for about a second before the first spin wipes it.
    //
    // The round is entered normally and everything else works; only the
    // automatic spin is withheld. Set back to false before shipping.
    FS_HOLD_ON_FIRST_ROUND: false,
    // ── TEMPORARY. Two popups that are almost impossible to reach by playing ──
    //
    // Both replace the TOTAL WIN popup at the end of a free-spins round, and
    // both sit at an extreme of the win range, so neither had ever been seen
    // on a real round. These force them. Set BOTH back to null before
    // shipping — they change what the player is paid and told.
    //
    // A number here overrides the "win is lower than 1x bet" threshold that
    // opens BETTER LUCK NEXT TIME (see Games/Shared/freeSpinOutroPopup). Set
    // it high — 1000 — and virtually every round ends on that popup instead of
    // the total. null uses the GDD's 1x.
    FS_BETTER_LUCK_BELOW_X: null,
    // A number here replaces MAX_WIN_MULTIPLIER when the client decides a round
    // hit the cap, and when it clamps the counter to it. Set it low — 5 — and
    // any round paying 5x bet or more ends on MAX WIN.
    //
    // ── null is the ONLY correct shipping value, and not merely tidiness ──
    //
    // The real figure is the operator's, and it is already wired:
    //
    //   window.__OPTIONS__.math.max_multiplier      30000, from the WRAPPER
    //     -> PROVIDER_CONFIG.MAX_WIN_MULTIPLIER     RGS/activeProviderConfig
    //     -> FREE_SPINS.MAX_WIN_MULTIPLIER          below
    //     -> freeSpinsCapForBet()                   Games/Shared/freeSpinOutroPopup
    //
    // NOT from the `init` response — that payload carries no max_multiplier.
    // If the number is ever wrong, the wrapper is where to look.
    //
    // A number here does not just "add a max win", which is what this comment
    // used to claim. It makes the CLIENT end a round the SERVER has not:
    // there is no "end feature" command, so the server keeps offering only
    // `freespin` while we have gone back to base, the next `spin` is refused
    // by our own guard with 202, and a reload lands in the room and straight
    // out of it again. Vladimir hit exactly that on 2026-09-22 with this set
    // to 5.
    //
    // At the REAL cap none of that happens, proven live the same day on round
    // 1473622: the server clamps balance.game to exactly bet x 30000, cuts the
    // remaining spins, and returns flow.state "closed" with `spin` offered —
    // all in one response. Our arithmetic agrees on that same response, so the
    // two can never disagree. See docs/api-contract-mad-labs-freespins.md §10.
    FS_MAX_WIN_MULTIPLIER: null,
  },
  BACKGROUND: {
    QUADRANT_SPRITES: false, // If true: renders 4 static background quadrant images (base/feature). If false: spine-only background
    FULLSCREEN_CLICK_SPRITE: true, // If true: renders a full-screen invisible sprite for background click detection (fullscreen toggle, etc.)
    REELSET_BONE_INJECTION: false, // If true: reelset is injected into background spine bones (base_reel/feature_reel) and counter-scaled. If false: traditional centered reelset
  },
  /**
   * GameView — unified spine scene renderer.
   * When ENABLED, replaces BOTH BackgroundAnimation AND GameContainer in Stage.js.
   * The game_view spine handles: background, reel frame, masks, dividers, jackpots.
   * ReelSet, OverlayReelSet, DimReelSet are injected into spine bone slots.
   * When DISABLED, the old BackgroundAnimation + GameContainer architecture is used unchanged.
   */
  GAME_VIEW: {
    ENABLED: true,
  },
  /**
   * Intro screen — controls whether the intro_screen spine is loaded and used.
   * When false, IntroPopup renders a static black screen + "Tap to start" text.
   * Secondary theme (Zeus) does not have a game-specific intro spine yet.
   */
  INTRO_SCREEN: {
    SPINE_ENABLED: false, // If true: intro_screen spine loaded and played. If false: static fallback used.
  },
  /**
   * SlotRegistry — centralized PIXI container injection into spine bone slots.
   * Master toggle gates the entire system; sub-toggles control individual injection points.
   * When ENABLED is false, zero registration/subscription overhead — as if the context doesn't exist.
   */
  SLOT_REGISTRY: {
    ENABLED: true, // Master kill switch — when false, entire SlotRegistry is no-op
    REELSET_INJECTION: false, // Controls base_reel + feature_reel bone injection registration
    LOGO_INJECTION: false, // Controls all 4 logo bone slot registrations
    JACKPOTS_INJECTION: false, // Controls jackpots_position slot injection registration
    RESPIN_COUNTER_INJECTION: false, // Controls bonus_counter slot injection registration
    GAME_VIEW_INJECTION: true, // Controls GameView attachment_slot_placeholder registrations
  },
  CHARACTER_ANIMATION: {
    ENABLED: false, // If true: character spine is loaded, created, and animated alongside the background
  },
  SPINE_ANIMATIONS: {
    LANDING_ENABLED: true, // Enable landing animations
    IDLE_ENABLED: true, // Enable idle animations
    TURBO_LANDING_ENABLED: false, // If false: turbo mode skips landing/idle animations entirely (shows static textures, zero spine CPU cost)
    SHARED_SPINE_SKINS: true, // If true: symbols with a 'skin' field in SymbolData apply skin to shared spine instance (e.g., all LP symbols share one spine file with different skins)
    // Cleopatra's Fire: false — each symbol goes land → idle as soon as ITS reel
    // stops (Vladimir, 2026-09-28). true = Zeus round-level: landings hold on their
    // last frame and idle starts only after ALL reels stop, and only on a single
    // no-win spin (not autoplay, turbo or a feature trigger).
    ROUND_LEVEL_IDLE: false,
  },
  DIVIDERS: {
    ENABLED: false, // If false: reel dividers are not rendered or loaded (some games don't use them)
  },
  FREE_SPINS: {
    ENABLED: false,
    ROUNDS: 15, // Number of free spins awarded per trigger
    MAX_WIN_MULTIPLIER: PROVIDER_CONFIG.MAX_WIN_MULTIPLIER, // Max win cap — the FE guard's figure, same source POT_COLLECTION uses
  },
  FREE_ROUNDS: {
    ENABLED: true, // If false: free rounds feature is completely disabled
  },
  // ── The LIGHT anticipation ──
  //
  // Not TENSION below, which is the full system — dimmed reels, tension reel
  // spines, a reelset zoom, a background animation, its own sounds and a
  // per-reel deceleration — and is switched off in this game. Milos, demo 2:
  // "No anticipation when scatters land on reels 2 and 3. In the Main Game, if
  // 2 land the game will go into anticipation mode."
  //
  // Cleopatra's Fire (GDD §6.1): once one scatter short of the trigger has
  // landed, every reel after it anticipates ONE BY ONE, each in full —
  // game_view's anticipation_in → anticipation_loop × LOOPS, the reel stops,
  // anticipation_out, and only then the next reel. The Zeus tension principle,
  // at EVERY speed (turbo included). The reels do not slow down; a chain reel
  // just keeps spinning until its turn ends. See Games/Shared/
  // scatterAnticipation and stopReels in ReelControlContext.
  //
  // Every number it needs — which scatters, which reels, how many — comes from
  // PROVIDER_CONFIG.FREE_SPINS_TRIGGER, so there is nothing to keep in step.
  // Cleopatra's Fire (GDD §6.2, Hold & Win anticipation): the SAME chain as
  // the scatters — same clips, same one-reel-at-a-time turns, same dim, same
  // skip press — only the trigger differs. Coins count on every reel; once
  // START_AT are down and the reels left can still bring the rest of
  // PROVIDER_CONFIG.HOLD_AND_WIN_TRIGGER.REQUIRED_COINS (6), every reel after
  // that anticipates, ending on the reel that brings the 6th. START_AT 4 is
  // Griffin's Vault's coin tension (MIN_SYMBOLS 4, CUTOFF 6) and the GDD's
  // counter example ("4 / 6 Fireballs"). Timing and dim settings are the
  // ones in SCATTER_ANTICIPATION below. See Games/Shared/scatterAnticipation.
  COIN_ANTICIPATION: {
    ENABLED: false,
    START_AT: 4,
  },

  SCATTER_ANTICIPATION: {
    ENABLED: true,
    // How many times anticipation_loop (1 s) plays on a reel before it stops.
    // Per reel: in 0.67 s + LOOPS × 1 s, the landing, then out 0.67 s.
    LOOPS: 1,
    // Dim the reels still waiting their turn — one column of the win dim
    // (DimReelSet) per reel: its 0.6 black and its 250 ms fades.
    DIM_REELS: true,
    // NOT USED since the dim became DimReelSet's per-reel columns (2026-09-29):
    // DIM_TINT: 0x666666,
    // Wait before the waiting reels darken, ms — lets the anticipation_in
    // (667 ms) develop first. Undimming is always immediate.
    DIM_DELAY_MS: 250,
  },


  TENSION: {
    ENABLED: false, // Master switch — enables the entire tension system (animations, spines, sounds, reel deceleration)
    SYMBOL_COUNTING_ENABLED: false, // Frontend symbol-counting trigger — counts coins/scatters on landed reels to start tension. Disabled when API-driven anticipation is the sole trigger source.
    REELSET_ANIMATION: false, // If true: plays reelset animation (e.g. zoom) during tension
    ENVIRONMENT_ZOOM: false, // If true: background and scene transition containers zoom in sync with reelset
    BACKGROUND_ANIMATION: false, // If true: plays background spine animation during tension
    TRIGGER_SYMBOL: BONUS_SYMBOL_IDS, // Array of symbol IDs that trigger tension — coins (11-25), jackpots (27-29), royal (26)
    SCATTER_SYMBOL: SCATTER_SYMBOL_IDS, // Array of scatter symbol IDs that trigger free spin tension
    MIN_SYMBOLS: 2, // Minimum trigger symbols found to trigger anticipation mode (anticipate the 3rd scatter)
    CUTOFF_THRESHOLD: 3, // Stop tension mode when this many trigger symbols are reached (triggers feature round)
    DURATION: 2667, // Fallback duration (ms) — overridden at runtime by spine's actual tension_in + tension_idle + tension_out durations
    IDLE_LOOPS: 0.4, // ~1000ms per reel (in + idle×0.4 + out ≈ 233+533+233 = 999ms)
    REEL_DECELERATION: {
      ENABLED: true, // If true: tension reels gradually decelerate from full speed to TARGET_SPEED (GDD §10.3)
      DECELERATION_RATIO: 1.0, // Fraction of tension cycle to spend decelerating (1.0 = decelerate through entire cycle)
      TARGET_SPEED: 0.5, // Final spin speed as fraction of normal (0.25 = 25% = 4× slower)
      // Landing velocity is mathematically matched to the decelerated spin speed.
      // TENSION_LANDING_SLOWDOWN compensates for the back-out overshoot feeling faster against the slow spin.
      TENSION_LANDING_SLOWDOWN: 1.0, // Multiplier on landing duration (1.0 = exact velocity match, >1.0 = softer landing)
    },
    // API-driven anticipation (GDD §10.2) — forces ALL 5 reels into tension when RGS flags the spin.
    // Cascade timing is reactive: each reel starts the next via onReelLanded.
    ANTICIPATION: {
      ENABLED: false, // Master toggle — enables anticipation system
      FORCE: false, // Dev toggle — forces anticipation on every spin (set true during development)
    },
  },

  WIN_LINES: {
    ENABLED: false,
  },
  WIN_BOX: {
    // ENABLED: false, // Off for Cleopatra's Fire — the game will present wins another way. Skips the win_box spine load, the scene mounts and every call in both win controllers.
    ENABLED: true, // The Midnight Alchemy winbox (spines/winbox) — base and free spins.
    WINNING_ONLY: true, // true = boxes only on winning symbol positions, false = full line path
    SNAP_TO_LOOP: true, // true = win boxes play exact loop count matching display duration (no partial loops)
  },
  WIN_LINE_DIGIT: {
    ENABLED: true,
  },
  // Win animation timing configuration
  // ⚠️  CRITICAL INVARIANT: The master duration comes from the highest-priority symbol's
  //     actual spine "win" animation (e.g. w0/wild = 2000ms). All other symbols' loops
  //     are integer multiples of this master (LP/MP 1000ms × 2 = 2000ms).
  //     DO NOT scale or halve the master — it breaks frame-0 alignment on loop boundaries.
  WIN_ANIMATION_TIMING: {
    SPINE_LOOP_DURATION: 1000, // Fallback only — used when spine instance not yet available. Real duration comes from the spine data at runtime.
    DURATION_SCALE: 1.0, // ⚠️  MUST be 1.0. Multiplier on the master symbol's win animation. Changing this cuts the loop short (e.g. 0.5 = wild plays half its animation).
    SPIN_UNBLOCK_DELAY: 1000, // Fixed delay (ms) before spin button unblocks after a win. Independent of animation duration.
    WIN_LINES_DELAY: 250, // Delay before showing win animations after spin completes (in ms)
    FREE_SPIN_WIN_LINES_DELAY: 400, // Delay before showing win animations in free spins (in ms)
    // LOOPS_PER_LINE: 1, // Number of master-duration loops per winning line during line cycling
    // 2: one loop cycled the lines "way too fast" (Vladimir, 2026-09-30).
    LOOPS_PER_LINE: 2, // Number of master-duration loops per winning line during line cycling
    get LINE_CYCLE_DURATION() {
      return this.SPINE_LOOP_DURATION; // Fallback duration for line cycling when spine instance not available
    },
    // Fast win animation for turbo/autoplay — shorter loop for snappier feel.
    // Falls back to regular "win" if the animation doesn't exist in the spine.
    //
    // ── OFF for Mad Labs ──
    //
    // This exists for Zeus, whose win animations are long enough that turbo and
    // autoplay needed a shortened clip to stay watchable. Mad Labs symbols are
    // already quick, so substituting `win_fast` only robbed them of the room
    // they need to read — a brief flash instead of a win.
    //
    // With it off, `useFastWin` is false everywhere, so every win plays the full
    // `win` animation at master duration on every speed. LOOPS below is kept but
    // inert; it only applies to a fast win, so it does nothing until this is
    // switched back on.
    WIN_FAST: {
      ENABLED: false, // If true: turbo/autoplay uses "win_fast" animation when available
      ANIMATION_NAME: "win_fast", // Spine animation name to look for (must be exact match)
      WIN_TEXT_SPEED_UP: true, // If true: auto-scales WinText spine to fit in→idle→out into fast win duration
      // How many loops a FAST win plays, replacing LOOPS_PER_LINE and
      // LOOPS_FOR_SHOW_ALL.
      //
      // `win_fast` is roughly half the length of `win`, so a single loop was
      // over before the player could read it — a brief flash and gone. Two
      // brings the fast win back to something visible while still being
      // noticeably snappier than the full one.
      //
      // Applies to the global show-all phase and to line cycling alike, so
      // whichever a round uses gets the same treatment.
      LOOPS: 2,
    },
  },
  SHOW_ALL_WINS: {
    ENABLED: true, // If true: shows all winning symbols at once with global win text before line cycling
    // How many loops the GLOBAL show-all holds for — every winning symbol up at
    // once, with the total, before line cycling.
    //
    // 2, not 1. One loop of this game's `win` is over before the player can
    // take it in, which read as a flash and then straight into the next
    // autoplay spin. This is the knob that governs it now that WIN_FAST is off;
    // WIN_FAST.LOOPS only ever applied to the shortened clip.
    LOOPS_FOR_SHOW_ALL: 2, // Number of animation loops for "show all wins" phase (event-based timing)
    // Turbo and super turbo hold the global win for ONE loop, not two. The
    // player has asked to move quickly; two loops of the full `win` there reads
    // as the game ignoring them. Line cycling is already 1 loop on every speed.
    LOOPS_FOR_SHOW_ALL_TURBO: 1,
    get DISPLAY_DURATION() {
      return FEATURES_CONFIG.WIN_ANIMATION_TIMING.SPINE_LOOP_DURATION; // 1500ms - matches spine loop (fallback for timeout)
    },
    EXCLUDE_SCATTER: true, // If true: scatter wins skip "show all wins" and go directly to line cycling
  },
  EXPANDING_WILDS: {
    ENABLED: true, // If false: expanding wild feature is completely disabled, win lines start immediately after spin
    MULTIPLIER_ENABLED: false, // If false: expanding wild multiplier feature is disabled (no multiplier sounds or visuals)
    ANIMATION_MODE: "cascade", // "cascade" | "full_reel" | "instant" - cascade: symbol-by-symbol, full_reel: spine overlay, instant: no animation
    CASCADE_OVERLAP_PERCENTAGE: 0.1, // Percentage of "appear" animation duration before triggering next symbol (0.1 = 10% overlap, event-aware timing)
    SHOW_ALL_WINS_BEFORE_CYCLING: true, // If true: shows all winning symbols after expanding wild animation (overrides SHOW_ALL_WINS.ENABLED)
    // If false: uses SHOW_ALL_WINS.ENABLED setting to determine behavior
    LOOPS_FOR_EXPANDING_WILD_SHOW_ALL: 1, // Number of animation loops for "show all wins" after expanding wild (event-based timing)
  },
  TURBO_SUGGESTION: {
    ENABLED: Settings.APP.UI_THEME === "primary", // Only primary theme has turbo suggestion popup
  },
  RESPONSIBLE_GAMBLING: {
    ENABLED: Settings.APP.UI_THEME === "secondary", // Enabled for secondary theme, disabled for primary
  },
  HEARTBEAT: {
    ENABLED: false, // If false: heartbeat manager disabled, no periodic balance sync
    INTERVAL: 30000, // Heartbeat interval in milliseconds (30 seconds)
    START_DELAY: 30000, // Delay before first heartbeat (30 seconds)
    PAUSE_DURING_SPIN: true, // Pause heartbeat during active spins
  },
  MINI_PAYTABLE: {
    ENABLED: false, // If false: mini paytable (symbol info popup on long-press) is completely disabled
  },
  BONUS_BUY: {
    ENABLED: false, // If false: bonus buy button and popup completely disabled
  },
  // Cleopatra's Fire: the Pot is the fire cup on the logo (GDD §5.1). Every
  // coin that lands throws a fire trail at the logo's centre, the logo plays
  // `logo_hit`, and line wins wait for it. Base game only for now. The trail
  // is Zeus's `trail_coin` (effects/trail/particle_trail.json) — see
  // Games/Shared/coinToLogoTrail. The simple replacement for Zeus's
  // POT_CHARGING lightning below, which stays off.
  LOGO_POT: {
    TRAILS_ENABLED: false,
    // Biggest particle, as a share of the symbol height (live, every resize).
    // The spawn spread follows it, so this is the trail's thickness.
    // 0.3 read thin; doubled (Vladimir, 2026-09-28).
    TRAIL_SIZE: 0.6,
    // Fire: a hot head burning out to deep orange-red (additive blend).
    TRAIL_TINT_START: 0xfff1a8,
    TRAIL_TINT_END: 0xff3c00,
    // Flight time, ms — coin to logo.
    TRAIL_DURATION_MS: 350,
    // Each particle's life, s — the tail's length (trail_coin ships 0.2).
    TRAIL_PARTICLE_LIFE: 0.3, // was 0.2 — a longer tail for the fireball shape (2026-09-29)
    // The particle budget. Alive per trail ≈ COUNT × RATE × LIFE
    // (30 × 60 × 0.2 = 360). RATE caps spawns per second, so a 120 Hz screen
    // draws the same as a 60 Hz one (as authored it drew double: 720).
    // COUNT 10 read thin and poor (Vladimir, 2026-09-28) — back to the
    // authored 30.
    TRAIL_SPAWN_COUNT: 30,
    TRAIL_SPAWN_RATE: 60,
    // The arc: ONE even arc, like a throw, that comes down into the bowl.
    // Its height is a share of the horizontal distance, so near and far
    // trails have the same shape and landing angle. Its top stays on screen.
    //   HEIGHT    control height as a share of the horizontal distance —
    //             higher = a taller arc and a steeper fall into the logo
    //   LIFT      a little extra, in symbol heights
    //   TARGET_OFFSET_Y  fine-tunes the target up from where it lands: the
    //             logo's centre across, at its fire bone's height. 0 = there.
    TRAIL_ARC_HEIGHT: 0.5,
    TRAIL_ARC_LIFT: 0.2,
    // How far inside the screen's top edge the arc's top may reach, in
    // symbol heights (0.1 touched the edge).
    TRAIL_TOP_MARGIN: 0.15,
    TRAIL_TARGET_OFFSET_Y: 0,
    // ── The fireball's SHAPE: a round head, a pointed tail ──
    // Each particle is born at the head at HEAD size and shrinks to TAIL size
    // as it falls behind — so the head is a ball of the freshest, biggest
    // particles and the trail tapers to a point. [min, max], in the native
    // `trail_coin` scale (TRAIL_SIZE still sets the biggest one on screen).
    // It used to be the other way round (0.1–0.2 growing to 0.3–0.5): thin at
    // the head, fat at the tail.
    TRAIL_HEAD_SCALE: [0.3, 0.5],
    TRAIL_TAIL_SCALE: [0.03, 0.06],
  },
  POT_CHARGING: {
    ENABLED: false, // If true: lightning trails fire from Zeus symbols to matching POT bones on reel landing
    TRAIL_DURATION: 160, // Travel time for lightning trail (ms) — 120 charge + 160 travel + 120 impact = 400ms total
  },
  SUPER_TURBO: {
    ENABLED: true, // If true: super-turbo speed mode available (base game only, skips regular win animations)
  },

  GAMBLE: {
    ENABLED: false, // THE gamble switch. Off: Cleopatra's Fire has no gamble. Gates the dialog AND every gamble path in the PopiPlay RGS (isGambleEnabled). Mad Labs had it on.
    // ── Card conveyor ──
    // The strip inside the machine. Motion is SymbolStripSpinner, the same
    // class the gamble scatter uses, which is ReelControllerV2's mechanics with
    // per-instance geometry. Everything here maps onto a reel knob of the same
    // name in SPIN.AUTO_PLAY_SPEEDS.
    VISIBLE_CARDS: 5, // Cells on screen at rest. Drives cell count (N×2+2) and stop travel (N cells)
    CARD_GAP: 3, // Space between cards in authored spine units (0 = touching)
    NUMBER_SCALE: 1.3, // Spin count vs its authored 76 — a card has far more room than the scatter
    // ── The belt is a SEQUENCE, not a weighted bag ──
    //
    // A run of rats, then exactly one card, so the ratio AND the ordering are
    // both decided. A share alone gave neither: at 60/40 three cards in a row
    // came up 6.4% of the time at every position along the belt, and the gamble
    // read as a parade of prizes.
    //
    // [min, max] cells, inclusive, drawn per run. CARD_RUN is the promise that
    // matters and it is now exactly ONE: cards are NEVER consecutive, at all.
    // Vladimir, 2026-09-22: "there are no consecutive cards EVER" — 2 rats,
    // 1 card, 3 rats, 1 card, 4 rats, 1 card. Varied, but TIGHT: a run of six
    // read as a dead belt. *"we cant allow 7 or 8"*, then *"we can also have 5
    // but not more than that"* — this ceiling is what decides it.
    FAIL_RUN: [2, 5], // rats between one card and the next
    CARD_RUN: [1, 1], // one card, then the rats come back — never two
    // ── ...and WHICH card, for the same reason ──
    //
    // The run structure decided how OFTEN a card shows. This decides which one,
    // and it had the same fault the ratio used to: drawn uniformly from
    // `variants`, a tier's share of the belt came from how many spin-counts it
    // happens to list, not from how likely it is. Holding purple, the backend
    // lists three reds and three golds, so gold — the rare one — rode past half
    // the time. Milos, demo 2: "almost all passing options are GOLD. This is
    // the way less likely option to get than RED."
    //
    // So the tier is drawn first and the value second, and each rung UP is this
    // many times rarer than the one below it. Two rungs give a share of
    // 1 / (1 + N), so the number IS the split: 9 is 10% and 19 is 5%. Holding
    // purple that is red 95% / gold 5% — Vladimir, 2026-09-22. Holding red
    // there is only gold left to win, so the belt is gold whatever this says —
    // correct, and not something to paper over.
    //
    // A PRESENTATION decay, not the real odds. Nobody has given us those, and
    // the belt has never been a statement of them — it says "almost always the
    // next rung, rarely the one beyond". Raise it to make gold rarer still.
    TIER_RARITY_FALLOFF: 19,
    // OFF. It existed to stop two golds pairing up inside a CARD_RUN of 2, and
    // with CARD_RUN fixed at 1 no two cards touch at all any more. Left on it
    // would forbid a gold after a gold even with rats between them, which drags
    // gold below the 10% asked for — the rarity is the falloff's job now.
    NO_REPEAT_TOP_TIER: false,
    // Card share falls out of the two: 1 / (3.5 + 1) = 22%.
    // How long the strip runs under anticipation_idle before easing onto the
    // result. A FLOOR, not a duration: the strip stops on whichever finishes
    // later, this or the `gamble_freespins` response, so a fast reply still
    // gets its full moment and a slow one is never cut off.
    SPIN_DURATION: 4500,
    // A lost ladder closes the screen by itself after the lose loop has played
    // this many times. Both buttons stay dark — the round is already closed
    // server-side, so there is nothing either could send — and nobody is made
    // to wait beyond the animation saying what happened.
    //
    // ── ONE loop, not two ──
    //
    // `gamble_screen.lose` is 1333ms, so two of them held a dead screen for
    // 2.7 seconds after the answer was already on it. The clip says everything
    // it has to say the first time round; the second was the player waiting for
    // a decision that had already been made.
    LOSS_CLOSE_LOOPS: 1,
    // The same thing for the WIN side, and it had the opposite fault.
    //
    // A top-tier win closes itself too — the server empties `variants`, so
    // there is no decision left to offer. It used to close the moment the card
    // swap finished: out (133ms) + in (667ms) = 800ms, which is LESS than one
    // loop of `gamble_screen.win` (1333ms). The screen went out mid-celebration
    // and the player never saw what they had landed on.
    //
    // So the win is held for this many full loops AFTER the swap. At 1 the
    // result stands for 800ms + 1333ms, which is one complete pass of the win
    // clip and the landed card's own looping `win` underneath it.
    //
    // Only this path uses it. Any OTHER win keeps the screen open anyway,
    // because the player still has collect-or-gamble to answer (GDD §9).
    WIN_CLOSE_LOOPS: 1,
    // Wheel-of-fortune wind-down. The strip never holds a flat speed: it
    // decelerates across the whole spin and the landing is velocity matched to
    // wherever it got to, so the slowing continues right into the stop.
    // Ported from Zeus's tension mode — see TENSION.REEL_DECELERATION and
    // ReelControllerV2.spinTension().
    SPIN_SPEED: {
      SPEED_MS_PER_SYMBOL_HEIGHT: 90, // ── THE SPEED KNOB ── ms to travel one card (higher = slower). Base game uses 46
      ACCEL_SPEED_RATIO: 2.889, // Acceleration duration = SPEED_MS × ratio (90 × 2.889 = 260ms)
      // No bounce — a straight fall into place, as SUPER_TURBO does on the
      // reels. With overshoot 0 the BackOut curve reduces to plain ease-out
      // cubic: it enters at speed and decays to a dead stop, never overshoots.
      BOUNCE_OVERSHOOT: 0,
      // 1.0 = TRUE velocity match. The landing then begins at exactly the
      // speed the strip is already travelling, so there is no jolt at the
      // handover. The reels use 0.693 to snap a bounce in tighter; this wants
      // the opposite, a long glide, so it keeps the honest number.
      STOP_SLOWDOWN_FACTOR: 1.0,
      LANDING_SLOWDOWN: 1.0,
      // Null = derive from the CURRENT speed at the moment of the stop:
      // (overshoot + 3) × distance / velocity × STOP_SLOWDOWN × LANDING_SLOWDOWN.
      // A number here overrides it outright, as TURBO/QUICK do on the reels.
      LANDING_DURATION: null,
      DECELERATION_DELAY: 3000, // Full speed for this long before the wind-down starts (ms)
      TARGET_SPEED: 0.5, // Speed at the end of the wind-down, as a fraction of full. Zeus tension uses the same

      WIND_UP_DISTANCE: 0.15, // Pull-back before release, as a fraction of one card
      WIND_UP_DURATION: 280, // Duration of that pull-back (ms)
    },
  },

  // ── Cleopatra's Fire Hold & Win — the per-cell spin ──
  //
  // Read by HoldAndWinCellController (Games/HoldAndWinBoard). Every cell
  // spins on its own. No wind-up (Hold & Win has none); LANDING_PEAK_OFFSET
  // matches the base reels so the landing sound fires where the symbol reads
  // as arrived. One speed only: no quick, no turbo (yet).
  HOLD_AND_WIN_SPIN: {
    // 126 -> 80 was "way too fast"; 100 is in between (Vladimir,
    // 2026-09-28). With no overshoot the landing is 3 × SPEED_MS ×
    // STOP_SLOWDOWN_FACTOR = 300ms.
    SPEED_MS_PER_SYMBOL_HEIGHT: 100, // ms to travel one cell (higher = slower). Base reels: 46
    ACCEL_SPEED_RATIO: 1.25, // Acceleration duration = SPEED_MS × ratio (100 × 1.25 = 125ms)
    // No bounce (Vladimir, 2026-09-28: "remove overshoot"). With 0 the
    // BackOut curve is a plain ease-out cubic: in at speed, dead stop.
    BOUNCE_OVERSHOOT: 0,
    BOUNCE_OVERSHOOT_MANUAL: 0,
    STOP_SLOWDOWN_FACTOR: 1.0, // 1.0 = the landing starts at exactly the spin speed
    STOP_SLOWDOWN_FACTOR_MANUAL: 0.75,
    // One cell's stop travel can collapse to ~0 after drift — floor the
    // distance so a slam-stop always drops in from above.
    MIN_STOP_DISTANCE_FRACTION_MANUAL: 0.75,
    TENSION_LANDING_SLOWDOWN: 1.0,
    LANDING_PEAK_OFFSET: 0.8, // Same as SPIN.AUTO_PLAY_SPEEDS.AUTO
    // ── The landing ends at the `in` clip's OPENING speed, so the two connect ──
    //
    // Cell heights per ms. From c.json `in`, the `coin` bone's translate: 0 at
    // t 0 -> y -14.19 at t 0.125, linear (no curve on the first key), i.e.
    // 113.5 skeleton units/s downward — the direction the strip travels. The
    // strip decelerates from its spin speed to exactly that (a cubic Hermite),
    // so the coin is still moving at the speed `in` continues at: no dead stop
    // between the strip and the clip. The coin's, because coins fill the grid;
    // one strip cannot land at each rig's own speed. Written as the two JSON
    // numbers so a re-export can be checked against it. null = the old BackOut.
    LANDING_EXIT_SPEED: 14.19 / 0.125 / Settings.SYMBOL_HEIGHT / 1000,

    // ── The respin round (HoldAndWinControlContext) ──
    // The cells spin at least this long, even when the response is quicker.
    RESPIN_MIN_SPIN_MS: 600,
    // Between one cell's stop and the next (column by column, top to bottom).
    // A quick stop lands everything still turning at once.
    RESPIN_STOP_GAP_MS: 40,
    // The first respin after the wipe has lifted — the player sees the room.
    RESPIN_FIRST_DELAY_MS: 800,
    // After the last cell has landed, before the next respin — the landed
    // spines' `in` (0.5 s) plays out first.
    RESPIN_NEXT_DELAY_MS: 700,
  },

  POT_COLLECTION: {
    ENABLED: false, // Pot Collection (Zeus Respins) — Zeus only, off for Mad Labs
    MAX_WIN_MULTIPLIER: PROVIDER_CONFIG.MAX_WIN_MULTIPLIER, // GDD §18.2 — sourced from providerConfig
    INTRO_DELAY: 500, // Delay before entrance popup after reels stop (ms)
    ENTRANCE_POPUP_MIN_DISPLAY: 3000, // GDD §16.4: 3s minimum before button appears
    MAX_COINS: 15, // GDD: 5 reels × 3 rows = 15 coin positions
    INITIAL_RESPINS: 3, // GDD §5.5: always 3 respins at start
    GRAND_JACKPOT_MULTIPLIER: 5000, // GDD §5.6: 5000× on grid fill
    GRAND_JACKPOT_POPUP_DELAY: 1000, // Delay before showing GJP popup after grid fill
    TOTAL_WIN_AUTO_CLOSE: 30000, // GDD §19: auto-close after 30s
    // Autoplay — inter-spin delay is a flat 180ms constant in PotCollectionControlContext
    // Symbol tension mode — per-symbol anticipation for last blank cell(s)
    // Universal pattern: any hold-and-win game can use this config shape.
    SYMBOL_TENSION: {
      ENABLED: true, // Enable per-symbol tension/anticipation
      DURATION: 4233, // How long the symbol spins in tension before stopping (ms) — 233ms IN + 4000ms LOOP to match sound
      TENSION_START_DELAY: 100, // Delay after last non-tension cell stops before starting tension (ms)
    },
    // Collection animation
    COLLECTION: {
      VALUE_TRAVEL_DURATION: 500, // Duration for value text traveling to Win Counter (ms)
      ROYAL_COLLECTION_DELAY: 500, // Delay before end-of-bonus collection starts
    },
    COLLECTOR_FONT_SIZE_MULTIPLIER: 0.15, // Collector counter text size as fraction of spine nativeHeight
    // Spin speed config — bonus always runs at AUTO speed
    SPIN_SPEED: {
      SPEED_MS_PER_SYMBOL_HEIGHT: 126, // Slightly slower than base (120ms)
      STOP_SLOWDOWN_FACTOR: 1.0, // Landing speed vs spin speed (1.0 = exact velocity match)
      TENSION_LANDING_SLOWDOWN: 1.0, // No extra slowdown — landing matches decelerated velocity
      ACCEL_SPEED_RATIO: 1.25, // Acceleration duration = SPEED_MS × ratio (160 × 1.25 = 200ms)
      BOUNCE_OVERSHOOT: 1.75,
      BOUNCE_OVERSHOOT_MANUAL: 0.8, // Snappier landing on manual stop (less bounce)
      STOP_SLOWDOWN_FACTOR_MANUAL: 0.75,
      // N=1 stop travel can collapse to ~0 after drift — floor distance (not duration)
      // so slam-stop always drops in from above. Base game uses N=3 so rarely needs this.
      MIN_STOP_DISTANCE_FRACTION_MANUAL: 0.75,
      SPIN_DURATION: 400, // Spin loop duration before auto-stop (ms) — was 500
      SYMBOL_STOP_BASE_DELAY: 50,
      SYMBOL_STOP_CASCADE_DELAY: 200, // Target ~3500ms round: 500 + 50 + 200×14 + ~200 ≈ 3550ms
      SYMBOL_STOP_CASCADE_DELAY_MANUAL: 0, // All cells stop simultaneously on manual stop
      COIN_TRAVEL_DURATION: null,
      STAGGER_RATIO: 1.0,
      WIND_UP_ENABLED: true,
      WIND_UP_CASCADE_DELAY: 20,
      WIND_UP_SPEED_FACTOR: 0.4, // Multiplier on wind-up duration (<1.0 = snappier)
      WIND_UP_DISTANCE: 0.05,
    },
    // Feature animation speed overrides — controls default speed mode per feature.
    // When SLAM_STOP_BUTTON_ENABLED is false, player cannot skip features via spacebar/button.
    // Each *_ALWAYS_FAST flag forces that feature to run at slam-stop speed by default.
    // To restore original slam-stop behavior: set SLAM_STOP_BUTTON_ENABLED: true, all *_ALWAYS_FAST: false.
    FEATURE_SPEED: {
      SLAM_STOP_BUTTON_ENABLED: false, // Allow player to slam-stop features via spacebar/button
      ENTRY_SPAWN_TURBO: false,        // Entry sequence: coins spawn 1-by-1 (player can slam-stop to speed up)
      MYSTERY_ALWAYS_FAST: false,      // Mystery shuffle — false = always play full animation
      DOUBLE_ALWAYS_FAST: true,        // Double trails — true = all fire at once
      COLLECT_ALWAYS_FAST: true,       // Collect trails — true = all fire at once
    },
  },

  ROYAL_COLLECT: {
    ENABLED: false, // If true: Royal Collection feature is active (separate from Hold and Win)
    // Shared config (not speed-dependent)
    BLURRED_TEXTURES_ENABLED: true, // Enable blur crossfade during RC spins
    BLUR_VELOCITY_THRESHOLD: 0.6, // (inactive when BLURRED_TEXTURES_ENABLED is false)
    STOP_SLOWDOWN_FACTOR: 1.0, // Seamless transition from spin to stop
    // Autoplay
    AUTOPLAY_FIRST_SPIN_DELAY: 800, // Delay after transition before first spin (ms)
    AUTOPLAY_SPIN_DELAY: 550, // Delay between continuous spins (ms) — non-coin rounds only
    // Collection animation — coin/jackpot travel to royal
    COLLECTION: {
      COIN_TRAVEL_DURATION: 500, // Canonical travel duration for ALL collectibles (ms) — matches c.json collect animation (500ms)
      ROYAL_COLLECTION_DELAY: 500, // Delay before royal collection starts after last coin collection (ms)
      ROYAL_ARC_LATERAL_OFFSET: 1.0, // Lateral offset for royal arc as fraction of symbolWidth (wider = more circular)
      ROYAL_DESTINATION_Y_OFFSET: -0.8, // Y offset as fraction of symbolHeight above row 0 center
    },
    // Speed-dependent config — mirrors base game's AUTO_PLAY_SPEEDS pattern
    AUTO_PLAY_SPEEDS: {
      AUTO: {
        SPEED_MS_PER_SYMBOL_HEIGHT: 120, // Deliberate bonus spin feel (slower than main game)
        ACCELERATION_DURATION: 200, // Ease-out cubic ramp from 0→1 (ms)
        BOUNCE_OVERSHOOT: 1.1, // Landing bounce — matches base game AUTO
        LANDING_DURATION: 375, // Fixed landing animation duration (ms)
        SPIN_DURATION: 500, // Auto-stop delay after spin start (ms)
        SYMBOL_STOP_BASE_DELAY: 50, // Base delay before first cascading stop (ms)
        SYMBOL_STOP_CASCADE_DELAY: 400, // Delay between each symbol stop (ms)
        SYMBOL_STOP_CASCADE_DELAY_MANUAL: 75, // Manual stop cascade — fast but still visible one-by-one
        COIN_TRAVEL_DURATION: null, // null = use spine's collect animation natural duration
        STAGGER_RATIO: 1.0, // Sequential: each coin/royal completes full arc before next starts
        // Wind-up animation (AUTO speed only — QUICK and TURBO skip it)
        WIND_UP_ENABLED: true, // Enable per-symbol wind-up before spin starts
        WIND_UP_CASCADE_DELAY: 30, // ms between each symbol starting its wind-up (cascade effect)
        WIND_UP_DISTANCE: 0.15, // Wind-up pull distance as fraction of symbolHeight (tune for feel)
      },
      QUICK: {
        SPEED_MS_PER_SYMBOL_HEIGHT: 75, // A bit faster than AUTO (120ms)
        ACCELERATION_DURATION: 200, // Smooth ramp-up (matches base game)
        BOUNCE_OVERSHOOT: 1.0, // Landing bounce — matches base game QUICK
        LANDING_DURATION: 375, // Natural landing (matches base game)
        SPIN_DURATION: 175, // Spin loop before auto-stop (total perceived time ~630ms with cascade)
        SYMBOL_STOP_BASE_DELAY: 0, // No base delay
        SYMBOL_STOP_CASCADE_DELAY: 65, // Tight cascade — rapid-fire stops
        SYMBOL_STOP_CASCADE_DELAY_MANUAL: 25, // Near-instant manual stop
        COIN_TRAVEL_DURATION: 475, // Slightly faster than AUTO (565ms)
        STAGGER_RATIO: 0.5, // Next coin starts when current is 50% through its arc
      },
      TURBO: {
        SPEED_MS_PER_SYMBOL_HEIGHT: 66, // Slower spin, ~3 symbol heights in 210ms
        ACCELERATION_DURATION: 0, // Instant full speed
        BOUNCE_OVERSHOOT: 1.5, // Pronounced landing bounce
        LANDING_DURATION: 200, // Fast snap landing
        SPIN_DURATION: 210, // ~4 symbol heights of travel before stop
        SYMBOL_STOP_BASE_DELAY: 0, // No base delay
        SYMBOL_STOP_CASCADE_DELAY: 0, // All stop simultaneously
        SYMBOL_STOP_CASCADE_DELAY_MANUAL: 0, // Instant manual stop
        COIN_TRAVEL_DURATION: 435, // Fast travel with scaled animation (ms)
        STAGGER_RATIO: 0, // All coins launch simultaneously (no stagger)
      },
    },
  },
  JACKPOTS: {
    ENABLED: false, // Jackpot values displayed in GameView spine slots — Mad Labs has none
  },
  TOOLBAR: {
    TOP: {
      BACKGROUND_MODE: "back", // "front" = toolbar renders its own bg (on top of game), "back" = bg behind game (effects bleed over)
      TOOLBAR_COLOR: "color", // "color" = solid background, "transparent" = no background
    },
    BOTTOM: {
      BACKGROUND_MODE: "front", // "front" = toolbar renders its own bg (on top of game), "back" = bg behind game (effects bleed over)
      TOOLBAR_COLOR: "color", // "color" = solid background, "transparent" = no background
    },
    REELSET_WIN_DISPLAY: {
      BACKGROUND_MODE: "front", // "front" = win display bar renders on top of reelset, "back" = behind reelset
      TOOLBAR_COLOR: "color", // "color" = solid background with rounded corners, "transparent" = no background
    },
  },
  IOS_FULLSCREEN_OVERLAY: {
    ENABLED: true, // If false: iOS Safari fullscreen swipe overlay is completely disabled
    DEV_MODE: false, // If true: bypasses iOS/Safari detection — shows overlay in any browser for testing (Chrome DevTools etc.)
  },
  LANGUAGE_SWITCH: {
    ENABLED: false, // If false: language selector in settings is hidden (no localization support yet)
  },
  TURBO: {
    SPIN_SENSITIVITY: 400, // Time to hold button before turbo mode activates (ms)
  },
  SPIN: {
    HIDE_IDLE_BUFFERS: true, // If true: buffer sprites (above/below visible area) are hidden when reels are idle to prevent overflow bleed
    // Cleopatra's Fire: ON — every coin on the base / free-spins reels shows
    // its value (GDD). Drawn with the Hold & Win label sprite (coinLabel), not
    // the Zeus BitmapText; values from round.coinValues (Games/Shared/coinValues).
    // COIN_VALUES_ON_REELS: false, // If true: create BitmapText for coin value display on reel sprites (set false when coins only appear in bonus modes)
    COIN_VALUES_ON_REELS: true,
    // ── ON, and it is now a SWAP rather than a crossfade ──
    //
    // It was off because the old implementation drew BOTH sprites for the whole
    // spin — the blurred one faded in while the sharp one was floored at alpha
    // 0.001 instead of hidden. That is 140 sprites a frame instead of 70 on a
    // 6-row grid, twice the fill in the reel window, and 34 textures against a
    // batch limit that is 16 on most GPUs and 8 on some mobiles.
    //
    // _updateBlurFromVelocity now swaps at the midpoint of the same velocity
    // ramp instead: exactly one sprite per cell is ever drawn, at alpha 0 or 1,
    // so blur costs one extra texture set in memory and NOTHING per frame. The
    // crossfade was never visible anyway — at 46ms per symbol it spanned about
    // two frames.
    BLURRED_TEXTURES_ENABLED: true, // If false: no blur crossfade during spin (use when blurred textures/animations are unavailable)
    // Programmatic blurred texture generation (replaces manual PS export + spritesheet)
    BLURRED_TEXTURE_GENERATION: {
      // BUILDS the blurred set at load: one batched draw per symbol, kept in
      // GPU memory for the session. Boot cost only — nothing here runs during
      // a spin.
      ENABLED: true, // If true: generate blurred textures from spine at load time. If false: use traditional blurred spritesheet
      SCALE_X: 1.0, // Width scale (1.0 = no change)

      // ══ THE ONLY KNOB FOR THE SPINNING SYMBOLS ══
      //
      // How far the symbol travels during the exposure, as a SHARE of the
      // cell's height. Not a pixel count: the extracted texture is a different
      // size for every symbol, so a fixed number of pixels would be a
      // different smear on each of them.
      //
      // The squash used to be a second knob (SCALE_Y below) and the two fought
      // each other — raise the smear and it clipped, raise the squash and the
      // symbol lost height for nothing. It is DERIVED now: the symbol is left
      // as tall as it can be while this smear still fits, so the only thing to
      // set is how blurred you want it.
      //
      //   0.21  current — symbol sits at ~86% height, was 75% for less smear
      //   0.30  heavy motion, symbol down to ~78%
      //   0.15  a hint of motion only, symbol nearly full height
      //   0.10  barely there — fits the extractor's own padding, no squash
      //
      SMEAR_RATIO: 0.21,

      // ── Below here is the blurred NUMBER FONT only ──
      //
      // FontGenerator still blurs the coin-value glyphs with PIXI's BlurFilter
      // and reads both of these. That is a different job on much smaller text,
      // it is not banding, and it was not what needed fixing. The SYMBOL path
      // ignores both: it derives its own squash, and it draws a real motion
      // smear rather than a blur — see
      // SpineTextureExtractor._createBlurredTexture for why.
      SCALE_Y: 0.75,
      BLUR_STRENGTH: 26,
    },
    // Wind-up animation settings
    WIND_UP_DURATION: 55, // Duration of wind-up animation per reel (ms) - set to 0 to disable wind-up. Fallback when velocity formula unavailable.
    // ── Wind-up, re-derived for a 6-row grid ──
    //
    // Both of these scale off STOP_MOVE_DISTANCE_SYMBOLS, which IS
    // SYMBOLS_VISIBLE — so moving from Zeus's 3 rows to 6 silently doubled the
    // pull and the duration. It came out at 0.54 of a symbol height over
    // 443ms, which reads as a drag, and it swallowed the whole minimum spin
    // (see SPIN_DURATION below).
    //
    // The formula is untouched; only the two multipliers on top of it.
    // ── The wind-up's DURATION — the feel knob ──
    //
    // Derived, not set: totalStopDuration x bounceFraction x this factor,
    // where totalStopDuration is (overshoot+3) x SYMBOLS_VISIBLE x SPEED_MS x
    // STOP_SLOWDOWN. So changing the landing changes the wind-up unless this
    // is re-tuned.
    //
    // ZEUS's own number. Nothing to rescale: the landing it multiplies is
    // already back on Zeus's clock (392ms), so this lands on Zeus's wind-up.
    //
    //   factor   out leg   frames   return   total
    //   1.01      138ms      8.3     83ms    221ms   current — Zeus
    //   1.50      207ms     12.4    124ms    331ms
    //   2.00      276ms     16.6    166ms    442ms   was here; too long
    //   3.00      414ms     24.8    248ms    662ms
    //
    // 1.01 was called "too quick to read" once, while the pull was a different
    // depth and the landing was 450ms. Judge it against Zeus on screen, not
    // against the 2.00 that replaced it.
    //
    // The return leg is always 0.6x the out leg, so a slow pull automatically
    // releases faster than it pulled — the "settle at the top, then catch
    // speed on the way down" read. Frames matter as much as milliseconds: this
    // is the one sub-pixel move in the spin, and too few frames is what makes
    // it look stepped.
    // 0.898, not Zeus's 1.01. The wind-up is landing x bounceFraction x this,
    // and bounceFraction is 2s/(3(s+1)) — so it moves whenever EITHER the
    // bounce or the landing does, and both have, twice now: the bounce was
    // halved, then the drop was eased from 430ms to 522ms to take the speed
    // jump out of the landing. This number is what holds the wind-up at Zeus's
    // 138ms through all of it. Left at 1.09 the pull would have stretched to
    // 167ms along with the drop.
    //
    // It was 1.09 when STOP_SLOWDOWN_FACTOR was 0.412. If that moves again,
    // scale this by the inverse ratio — 0.898 x (0.5 / newFactor).
    WIND_UP_SPEED_FACTOR: 0.898, // Multiplier for computed wind-up duration
    // ── Pull DEPTH, measured against the BOARD, not the cell ──
    //
    // ZEUS's own number, and it needs no rescaling. The pull is
    // overshootFraction x SYMBOLS_VISIBLE x this, in cells — so as a share of
    // the BOARD the row count cancels and 2.0 gives Zeus's depth on any grid:
    //
    //   pull / board height     in cells
    //   Zeus      0.0894        0.268   (2.0 on 3 rows)
    //   here      0.0894        0.536   (2.0 on 6 rows — same share, same px)
    //
    // 3.36 sat here while BOUNCE_OVERSHOOT was 0.85. It was that number's
    // compensation, not a value of its own: with the overshoot back at Zeus's
    // 1.1 it would pull 1.7x deeper than Zeus. The two move together.
    // 4.0, not Zeus's 2.0. This is expressed as a multiple of the BOUNCE, so
    // halving the bounce halves the pull with it unless it doubles. It holds
    // the pull at 0.0894 of the board — Zeus's depth, and the one the wind-up
    // was tuned on.
    WIND_UP_DISTANCE_MULTIPLIER: 4.0, // Pull distance vs bounce overshoot
    REEL_START_DELAY: 50, // Fallback delay between each reel starting (ms) - used when REEL_START_DELAYS not defined
    WIND_UP_CHECK_BUFFER: 80, // Safety buffer for wind-up completion check (ms) — accounts for full wind-up + release animation

    // ── Per-reel start delays (ms) — one gap, repeated ──
    //
    // CONSTANT, and matched to REEL_STOP_DELAYS_AUTO below on purpose: the
    // reels leave on one rhythm and land on one rhythm, just at different
    // tempos (40 out, 140 back).
    //
    // This inherited Zeus's shrinking cascade — 50/7/25, giving gaps of
    // 50, 43, 36, 29 — on the argument that shrinking gaps read as the reels
    // "catching up". At 60fps that is 3.0, 2.6, 2.2 and 1.7 frames, so the
    // idea was always doing less work than it claimed, and the same shape put
    // on the stop (at three times the gaps) was plainly wrong there: the first
    // pair kept one tempo and the last three another. Equal everywhere is the
    // rule now, and it costs nothing here.
    //
    // ── AND FRAME-ALIGNED, which is the part that actually makes it smooth ──
    //
    // These delays are TickerTimeouts, and a TickerTimeout fires on the first
    // frame AT OR AFTER its target — so every gap is rounded UP to a whole
    // frame. A gap that is not a whole number of frames therefore alternates
    // between two frame counts, and equal milliseconds come out UNEVEN on
    // screen. 40ms is 2.4 frames at 60Hz, which lands as 3, 2, 3, 2.
    //
    // 50 divides both frame lengths exactly — 3 frames at 60Hz, 6 at 120Hz —
    // so every reel starts on the same beat on either display:
    //
    //   50/7/25  [0,50,93,129,158]   Zeus's, shrinking      60Hz: 3,3,2,2
    //   40 flat  [0,40,80,120,160]   equal ms, uneven       60Hz: 3,2,3,2
    //   50 flat  [0,50,100,150,200]  current                60Hz: 3,3,3,3
    //
    // Keep any future value a multiple of 50/3 (16.67ms) for the same reason.
    // WIND_UP_CHECK_BUFFER below still covers the last reel comfortably.
    REEL_START_DELAYS: generateLinearDelays(Settings.REEL_COUNT, 50),
    // ── Auto stop (ms) — when each reel BEGINS its drop ──
    //
    // CONSTANT, unlike REEL_START_DELAYS above. That difference is deliberate.
    //
    // The start cascade shrinks its gaps (50/43/36/29) and gets away with it
    // because the whole thing is over in 158ms — the shrink is below what the
    // eye separates, and all it leaves behind is a "catching up" feel. The
    // stop runs at three times those gaps, and there the same shrink stops
    // being a feel and becomes a fault: the first gap reads as one rhythm and
    // the last three as another, so the cascade sounds like it changes its
    // mind halfway across the board.
    //
    // A flat 140 spans exactly the same 560ms the 170/150/130/110 version did
    // — same coupling, same length, one rhythm.
    //
    // The drop itself is 522ms, so the gap is really a share of the drop: at
    // Zeus's 350 a reel was a third of the way down before the next one moved
    // and the five thuds were far enough apart to count. At 140 the next reel
    // leaves while the one before has barely started, and the board reads as
    // coming to rest once.
    //
    // FRAME-ALIGNED, for the same reason REEL_START_DELAYS is — see the note
    // there. These fire on TickerTimeouts, which round up to a whole frame, so
    // 140ms (8.4 frames at 60Hz) came out as 9, 8, 9, 8: equal milliseconds,
    // visibly uneven landings. 200 is 12 frames at 60Hz and 24 at 120Hz, so
    // every reel drops on the same beat on either display.
    //
    // Walked in from both ends. Zeus's flat 350 left holes between five
    // separate landings; 150 closed them but then the reels after the first
    // came down almost on top of each other, which reads as the board
    // collapsing rather than settling. 200 is the middle: the drop is 522ms,
    // so each reel is a bit under half way down when the next one leaves —
    // clearly linked, still five distinct events.
    //
    //   gap    delays                    last thud   60Hz frame gaps
    //   350    [0,350,700,1050,1400]     1400ms      Zeus, gappy
    //   250    [0,250,500,750,1000]      1000ms      15,15,15,15
    //   200    [0,200,400,600,800]        800ms      12,12,12,12  current
    //   150    [0,150,300,450,600]        600ms       9,9,9,9  too tight
    //   100    [0,100,200,300,400]        400ms       6,6,6,6  one drop
    //
    // Keep any future value a multiple of 50/3 (16.67ms). Every reel still
    // plays its own reel_stop thud, so this sets the drum roll's tempo too.
    REEL_STOP_DELAYS_AUTO: generateLinearDelays(Settings.REEL_COUNT, 200),
    // ── Manual stop (ms) — the player pressed the button ──
    //
    // Flat and frame-aligned, matching REEL_START_DELAYS exactly: the reels
    // come to rest on the same beat they left on.
    //
    // This was the last cascade in the game still changing tempo, and it was
    // the only one that changed it the WRONG way — a negative gapReduction, so
    // the gaps GREW: 50, 60, 70, 80, arriving as 3, 4, 4, 5 frames at 60Hz.
    // Against a wind-up and an auto stop that are now perfectly even, a stop
    // that drags a little further apart with every reel is the one thing left
    // that reads as unsteady.
    //
    //   50/-10/50  [0,50,110,180,260]   was — gaps grow, 60Hz: 3,4,4,5
    //   50 flat    [0,50,100,150,200]   current — 60Hz: 3,3,3,3
    //
    // 200ms rather than 260 also suits what a manual stop is for. The landing
    // itself is unchanged: this game has no manual bounce or slowdown override
    // (BOUNCE_OVERSHOOT_MANUAL and STOP_SLOWDOWN_FACTOR_MANUAL belong to Pot
    // Collection), so a manually stopped reel drops exactly like an auto one.
    REEL_STOP_DELAYS_MANUAL: generateLinearDelays(Settings.REEL_COUNT, 50),

    // Stop delay settings (fallbacks when REEL_STOP_DELAYS not defined)
    STOP_DELAY_MANUAL: 50, // Delay between reels for manual stop (matches REEL_START_DELAY for chronological order)
    STOP_DELAY_AUTO: 250, // Delay between reels for automatic stop (cascade effect)


    // Auto-play speed mode settings.
    //
    // ── HOW ZEUS'S NUMBERS TRANSFER TO A 6-ROW GRID ──
    //
    // 3 Orbs of Zeus is the reference spin and these values are its values.
    // Only one class of knob needed changing, and there is a test for which:
    //
    //   Does the formula behind it multiply by STOP_MOVE_DISTANCE_SYMBOLS
    //   (which IS Settings.SYMBOLS_VISIBLE)?
    //
    //   NO  -> copy Zeus's number, unchanged. Speeds, delays, floors and ratios
    //          are all in ms or in ms-per-cell, and a cell is a cell.
    //          A DISTANCE knob copies too: bounce and wind-up pull both come
    //          out as a share of the BOARD, which is what the eye measures,
    //          and the row count cancels.
    //   YES -> halve it. Only STOP_SLOWDOWN_FACTOR is in this class. The
    //          landing covers the whole board — 3 cells on Zeus, 6 here — so
    //          at Zeus's factor it takes twice as long. Halved, the drop lasts
    //          the same milliseconds and covers the same share of the screen.
    //
    // The wind-up needs nothing of its own: its duration is a fraction of the
    // landing, so it comes back to Zeus's 138ms as soon as the landing does.
    //
    // Do not re-tune these against each other. Change one and every derived
    // duration moves with it; the numbers are a set.
    AUTO_PLAY_SPEEDS: {
      AUTO: {
        // ── A FLOOR from spin start, not a duration ──
        //
        // The reels stop the moment the API answers, unless less than this has
        // passed; then they wait for it. So this is the shortest a round can
        // be, and on a fast connection it is what every round actually is.
        //
        // ── It is not a spin length, it is a BUDGET ──
        //
        // The fixed costs of getting moving are inside it. There used to be
        // two:
        //
        //   wind-up      220ms   (137 out + 82 back)   GONE — SKIP_WIND_UP
        //   acceleration 209ms   (SPEED_MS 46 x ACCEL_SPEED_RATIO 4.545)
        //
        // Whatever is left over is how long reel 1 CRUISES at full speed
        // before the stop begins. At Zeus's 450, with both costs still in, that
        // was TWENTY-ONE MILLISECONDS — one and a bit frames. The reel finished
        // speeding up and immediately started landing, which is the whole
        // reason the spin read as short. 850 bought that cruise up to 421ms,
        // longer than either fixed cost it sat between.
        //
        // ── Then the wind-up went, and gave its 220ms to the cruise ──
        //
        // Nothing was re-tuned for that: 850 minus one fixed cost instead of
        // two is a 641ms cruise, half again as long as the number was chosen
        // for. So the floor comes down by the wind-up's own cost to hold the
        // cruise where it was. The round is 200ms shorter; the FEEL of the
        // spin is the one that was tuned.
        //
        //   floor   reel 1 cruises   reel 1 lands   reel 5 lands
        //   450       241ms             1192ms         1992ms   the floor
        //   600       391ms             1342ms         2142ms   current
        //   650       441ms             1392ms         2192ms
        //   850       641ms             1592ms         2392ms   before SKIP_WIND_UP
        //
        // 450 is marked the floor because below it the cruise drops under the
        // 209ms acceleration that precedes it — the reel would spend longer
        // getting up to speed than it spends AT speed, which is the fault 850
        // was chosen to fix in the first place.
        // Every millisecond added here goes into the CRUISE row and nowhere
        // else — the acceleration is a fixed cost — so this is
        // the only knob that lengthens the spin itself rather than the landing.
        // It moves every reel equally, so the 800ms spread between the first
        // and the last is untouched. Free spins read the same number
        // (FreeSpinBasicControlContext), so both rooms move together.
        //
        // It also feeds the connection-popup timeout, which is expectedDuration
        // + NATURAL_SPIN_DELAY — raising it makes a false "no connection"
        // popup less likely, never more.
        SPIN_DURATION: 600,
        SPEED_MS_PER_SYMBOL_HEIGHT: 46, // ZEUS. ── THE SPEED KNOB ── ms to travel one symbol height (higher = slower)
        // ── HALF Zeus's bounce depth, on purpose ──
        //
        // The landing read as the whole reel being yanked down. 0.783 puts the
        // rebound at 0.0224 of the board against Zeus's 0.0447 — exactly half.
        //
        // NOT half the number: the overshoot fraction is 4s^3 / 27(s+1)^2, so
        // halving 1.1 to 0.55 would have cut the depth to 0.23x, not 0.5x.
        // Solve the curve, do not halve the input.
        //
        // The three knobs below are compensated so this changes DEPTH ALONE —
        // the landing still takes 392ms, the wind-up still 138ms and still
        // pulls 0.0894 of the board. See each one.
        // Cleopatra's Fire: 0.783 -> 0.9, "add overshoot a little bit"
        // (Vladimir, 2026-09-28). Depth 4s^3/27(s+1)^2: 0.0224 -> 0.0299 of the
        // drop, x1.33.
        BOUNCE_OVERSHOOT: 0.9,
        // Holds the drop at Zeus's 392ms. Two corrections live in this one
        // number, and both are forced:
        //
        //   / 2      the duration carries STOP_MOVE_DISTANCE_SYMBOLS, which IS
        //            SYMBOLS_VISIBLE, so 3 -> 6 rows doubles it
        //   overshoot  the formula is (overshoot + 3) x distance / velocity, so
        //            lowering the bounce SHORTENS the landing unless this rises
        //
        //   (0.783 + 3) x 6 x 46 x 0.5 = 522ms
        //
        // ── THIS NUMBER IS THE SPEED JUMP INTO THE DROP ──
        //
        // The stop is BackOut easing, whose speed at t=0 is (3 + s) x distance
        // / duration. Put the duration formula into that and everything
        // cancels but this factor:
        //
        //   speed entering the drop = spin speed / STOP_SLOWDOWN_FACTOR
        //
        // So it is not a "slowdown" at all, it is the reciprocal of a speed-UP.
        // At 1.0 the drop begins at exactly the cruise speed and the handover
        // is seamless; anything lower makes the reel jump the moment the stop
        // starts, and that jump is the "yank" the drop reads as.
        //
        //   F      drop     speed entering the drop
        //   0.412  430ms    2.43x cruise   the yank
        //   0.45   470ms    2.22x
        //   0.50   522ms    2.00x          current
        //   0.55   574ms    1.82x
        //   0.60   626ms    1.67x
        //   1.00  1044ms    1.00x          seamless, and far too slow to play
        //
        // Zeus itself runs 0.3465 (2.89x), so the whole house feel has this in
        // it — this game is now the gentler one deliberately.
        //
        // The whole term is 1044 x this, which is the arithmetic to dial it by.
        // CHANGING IT MOVES THE WIND-UP TOO: compensate WIND_UP_SPEED_FACTOR by
        // the same ratio, or the pull stretches with the drop.
        // Cleopatra's Fire: the figures above assume Mad Labs' 6 rows. At 4
        // rows the drop was (0.783 + 3) x 4 x 46 x 0.5 = 348ms — a third shorter
        // than tuned, hence "too fast and short". 0.55 with the 0.9 bounce:
        // (0.9 + 3) x 4 x 46 x 0.55 = 395ms, back at Zeus's ~392ms; the speed
        // into the drop eases from 2.00x to 1.82x cruise. Wind-up is skipped,
        // so no WIND_UP_SPEED_FACTOR compensation is needed.
        STOP_SLOWDOWN_FACTOR: 0.55,
        // ── No wind-up. The reels leave on the press ──
        //
        // QUICK, TURBO and SUPER_TURBO have always set this; AUTO was the last
        // mode still pulling back before it released, and the pull read as lag
        // between the press and the board moving.
        //
        // SKIP_WIND_UP rather than WIND_UP_DURATION: 0. They are NOT the same
        // switch. Zero takes ReelControllerV2's early return straight into
        // `_startSpinAnimation`, which never dispatches `incrementWindupCount`
        // — so `spin.windupComplete` would stay false for ever and the play
        // button would never turn into a stop button. `spinNoWindUp` does
        // dispatch it, which is why the turbo modes have been safe on this
        // route for as long as they have used it.
        //
        // The wind-up MATHS below (WIND_UP_SPEED_FACTOR, WIND_UP_DISTANCE_
        // MULTIPLIER, WIND_UP_DURATION) is left intact and unused, so the pull
        // can be brought back by deleting this one line.
        SKIP_WIND_UP: true,
        AUTO_PLAY_DELAY: 100, // ZEUS. Delay between auto-play spins. NON-WINNING rounds only — a win pauses on its own counter instead. Read by ReelControlContext; the AUTOPLAY_SPIN_DELAY constants elsewhere in this file are NOT.
        AUTO_STOP_TIMEOUT: 400, // Time before automatic stop starts
        LANDING_PEAK_OFFSET: 0.8, // Multiplier on overshoot peak timing (1.0 = exact trough, <1.0 = fires earlier)
        ACCEL_SPEED_RATIO: 4.545, // ZEUS. Acceleration duration = SPEED_MS × ratio (46 × 4.545 = 209ms). No row count in it, so it copies
        BLUR_VELOCITY_THRESHOLD: 0.6, // Fraction of max speed at which blur activates (0.6 = 60%)
        // LANDING_DURATION removed — computed at runtime from velocity formula:
        // duration = (overshoot + 3) × distance / spinVelocity × STOP_SLOWDOWN_FACTOR
      },
      QUICK: {
        SPIN_DURATION: 90, // Shorter spin duration than AUTO (400ms vs 860ms)
        SPEED_MS_PER_SYMBOL_HEIGHT: 44, // Same as AUTO
        BOUNCE_OVERSHOOT: 1.0, // Same as AUTO
        AUTO_PLAY_DELAY: 500, // Same as AUTO
        AUTO_STOP_TIMEOUT: 500, // Same as AUTO
        ACCELERATION_DURATION: 200, // Same as AUTO
        BLUR_VELOCITY_THRESHOLD: 0.6, // Same as AUTO
        // LANDING_DURATION removed — computed at runtime from velocity formula:
        // duration = (overshoot + 3) × distance / spinVelocity × STOP_SLOWDOWN_FACTOR
        STOP_SLOWDOWN_FACTOR: 0.355, // ZEUS 0.710 / 2 — same halving as AUTO. Left at 0.710 this mode dropped for 750ms, twice Zeus's 375ms
        SKIP_WIND_UP: true, // No wind-up animation
        REEL_STOP_DELAYS: generateDecreasingDelays(
          Settings.REEL_COUNT,
          70,
          5,
          55,
        ), // Same as manual stop cascade for snappy feel
      },
      TURBO: {
        // Timing
        SPIN_DELAY: 150,
        AUTO_PLAY_DELAY: 16,
        AUTO_STOP_TIMEOUT: 16,
        MIN_SPIN_DURATION: 5, // Barely moves — almost instant snap to landing
        // ── How long a turbo press waits before it gives up and SPINS ──
        //
        // Turbo is a DROP, not a spin: press, the answer arrives, the reels
        // fall into place. That is the whole feel of the mode and it must not
        // change on a normal connection.
        //
        // The reels therefore stay idle for this long. If the response lands
        // inside the window — which it always does on a healthy connection —
        // nothing ever spins and the drop happens exactly as it always has.
        // Only when the server is genuinely slow do the reels start turning,
        // so the player sees the game working instead of a frozen board.
        //
        // 300ms: comfortably longer than a normal round trip, short enough
        // that a stall is covered before it reads as a freeze.
        SPIN_AFTER_WAIT_MS: 300,

        // Unified spin parameters (ticker-based turbo — same system as normal/quick)
        // 36 against AUTO's 42 — quicker without the reels smearing into each
        // other. 32 was tried and pushed too far; 26 and 28 both read as
        // glitchy on the first reel. The blur cannot keep up much past this.
        SPEED_MS_PER_SYMBOL_HEIGHT: 36,
        ACCELERATION_DURATION: 0, // Instant full speed
        // LANDING_DURATION removed — computed at runtime from velocity formula
        // ── HALF the bounce DEPTH, same as AUTO ──
        //
        // The turbo landing read as the whole reel being yanked downward. Zeus's
        // 1.0 put the rebound at 0.0370 of the board; 0.7169 puts it at 0.0185
        // — exactly half.
        //
        // NOT half the number. The overshoot fraction is 4s^3 / 27(s+1)^2, so
        // halving 1.0 to 0.5 would have cut the depth to 0.31x, not 0.5x. Solve
        // the curve, do not halve the input — the same rule the AUTO block
        // above records for its 1.1 -> 0.783.
        BOUNCE_OVERSHOOT: 0.7169,
        // Compensated so the change is DEPTH ALONE. The landing duration is
        // (overshoot + 3) x distance x speed x this, so lowering the bounce
        // SHORTENS the drop unless this rises to meet it:
        //
        //   (0.7169 + 3) x 6 x 36 x 0.5176 = 416ms — unchanged
        //
        // Was 0.481 against an overshoot of 1.0, for the same 416ms.
        STOP_SLOWDOWN_FACTOR: 0.5176,
        TENSION_LANDING_SLOWDOWN: 1.0, // No extra slowdown — turbo decelerated speed is still fast enough
        BLUR_VELOCITY_THRESHOLD: 0.6,
        SKIP_WIND_UP: true, // No wind-up animation
        // ── NO CASCADE. Turbo starts and stops as one wall ──
        //
        // Client feedback, demo 1: "the symbols should drop almost like a single
        // wall / simultaneously" and "the last two reels feel delayed". So the
        // cascade is removed outright — the same delays SUPER_TURBO uses, and
        // ONLY those. Every other number in this block is turbo's own and is
        // deliberately untouched: the speed, the drop, the bounce and the
        // landing all stay exactly as they were.
        //
        // ── What was tried before, and why it is not the answer ──
        //
        // The stagger was 16ms, then 32ms. 16 was never a cascade: the turbo
        // drop is ~416ms, so five landings at 16ms apart overlapped by 96% and
        // arrived as one ragged clump — the only thing separating them was each
        // reel's own drift compensation, which differs per reel because each
        // stops at a different sub-cell offset. 32 made it a real cascade over
        // 128ms, which read cleanly but is the very thing the client does not
        // want here.
        //
        // Zero is not "16 again". It removes the per-reel scheduling entirely,
        // so the reels cannot disagree by a frame; what is left is only the
        // drift compensation, which is sub-frame.
        REEL_START_DELAY: 0,
        REEL_START_DELAYS: generateLinearDelays(Settings.REEL_COUNT, 0),
        REEL_STOP_DELAY: 0,
        REEL_STOP_DELAYS: generateLinearDelays(Settings.REEL_COUNT, 0),

      },
      SUPER_TURBO: {
        // Slightly faster than TURBO — tuned for rapid-fire gameplay
        SPIN_DELAY: 75,
        AUTO_PLAY_DELAY: 16,
        AUTO_STOP_TIMEOUT: 16,
        MIN_SPIN_DURATION: 5,
        // Same rule as TURBO — see SPIN_AFTER_WAIT_MS there.
        SPIN_AFTER_WAIT_MS: 300,

        SPEED_MS_PER_SYMBOL_HEIGHT: 14,
        ACCELERATION_DURATION: 0,
        // LANDING_DURATION removed — computed at runtime from velocity formula
        BOUNCE_OVERSHOOT: 0, // No bounce — straight drop into place
        // ZEUS 1.020 / 2 → 3 x 6 x 14 x 0.51 = 128ms, Zeus's own drop. With
        // BOUNCE_OVERSHOOT 0 the BackOut curve reduces to a plain ease-out:
        // it enters at speed and stops dead, no rebound.
        STOP_SLOWDOWN_FACTOR: 0.51,
        TENSION_LANDING_SLOWDOWN: 1.0, // No extra slowdown — super turbo decelerated speed is still fast enough
        BLUR_VELOCITY_THRESHOLD: 0.6,
        SKIP_WIND_UP: true,
        REEL_START_DELAY: 0,
        REEL_START_DELAYS: generateLinearDelays(Settings.REEL_COUNT, 0),
        REEL_STOP_DELAY: 0,
        REEL_STOP_DELAYS: generateLinearDelays(Settings.REEL_COUNT, 0),

      },
    },
  },
  // ── Cosmetic coin generation during spin animation ──
  // Controls which symbols randomly appear while reels are spinning (visual only — landing symbols come from API).
  // Two modes:
  //   "royal_collection" — Max 1 coin per reel, coins only on COIN_REELS, royal only on ROYAL_REELS
  //   "hold_and_win"     — Multiple coins per reel allowed, coins on all reels (original H&W behavior)
  //   null               — No coins or royals generated during spin animation
  COIN_GENERATION: {
    MODE: null, // "royal_collection" | "hold_and_win" | null
    PROBABILITY: 0.2, // Chance per symbol position to become a coin (35% — tuning)
    ROYAL_PROBABILITY: 0.2, // Chance per symbol position to become a royal (middle reel only)
    COIN_REELS: GAME_LAYOUT.REEL_INDICES.filter(
      (i) => i !== GAME_LAYOUT.MIDDLE_REEL,
    ), // All reels except middle (adapts to REEL_COUNT)
    ROYAL_REELS: [GAME_LAYOUT.MIDDLE_REEL], // Middle reel only (adapts to REEL_COUNT)
    MIN_COIN_SPACING: 3, // Minimum positions between coins (3 = at least 2 non-coin symbols between coins, max 1 visible at a time on a 3-row grid)
  },
  HOLD_AND_WIN: {
    ENABLED: false, // If false: hold and win feature is completely disabled, no intro dialog will be shown
    MAX_WIN_MULTIPLIER: PROVIDER_CONFIG.MAX_WIN_MULTIPLIER, // Max win threshold multiplier — sourced from providerConfig
    INTRO_DELAY: 500, // Delay in ms before showing hold and win intro dialog after reels stop
    COIN_VALUE_FONT_SIZE_MULTIPLIER: 0.2, // Font size for coin values as percentage of symbol height
    // Spin configuration
    SPEED_MS_PER_SYMBOL_HEIGHT: 134, // Milliseconds to move one symbol height (lower = faster)
    BLURRED_TEXTURES_ENABLED: true, // If true: use blurred textures during spin, if false: use regular textures
    // Stop animation configuration
    STOP_SLOWDOWN_FACTOR: 0.33, // Stop speed relative to spin speed (lower = slower stop, 0.35 = ~3x slower than spin)
    BOUNCE_OVERSHOOT: 0.9, // Bounce effect on landing (0 = no bounce, 1.2 = medium bounce, 2.0 = strong bounce)
    // Stop timing configuration
    SPIN_DURATION: 500, // Total spin duration before automatic stop (milliseconds) - needs to be long enough for API response
    SYMBOL_STOP_BASE_DELAY: 50, // Base delay before first symbol stops in cascading (milliseconds) - gives overlay controllers time to initialize
    SYMBOL_STOP_CASCADE_DELAY: 300, // Delay between each symbol stop in cascading for automatic stops (milliseconds)
    SYMBOL_STOP_CASCADE_DELAY_MANUAL: 50, // Manual stop cascade delay for TURBO mode (milliseconds)
    SYMBOL_STOP_CASCADE_DELAY_MANUAL_NORMAL: 25, // Manual stop cascade delay for NORMAL/AUTO speed (milliseconds) - faster for better responsiveness
    // Autoplay configuration
    AUTOPLAY_FIRST_SPIN_DELAY: 800, // Delay after transition completes before first autoplay spin starts in normal mode (milliseconds)
    AUTOPLAY_FIRST_SPIN_DELAY_TURBO: 800, // Delay for turbo mode - quick start after transition (milliseconds)
    AUTOPLAY_SPIN_DELAY: 800, // Delay between autoplay spins in normal mode (milliseconds)
    AUTOPLAY_SPIN_DELAY_TURBO: 725, // Delay between autoplay spins in turbo mode
    // Symbol tension mode — per-symbol anticipation for last blank cell
    SYMBOL_TENSION: {
      ENABLED: true, // Enable symbol tension/anticipation for Hold and Win
      COIN_THRESHOLD: 19, // Trigger when this many coins are on reelset (remaining blanks = 20 - COIN_THRESHOLD)
      COIN_GENERATION_PROBABILITY: 0.3, // Probability of generating coins during tension (30% vs 12.5% normal)
      DURATION: 3500, // Duration that symbol spins in tension mode before stopping (milliseconds) - increased for more anticipation
      CASCADE_DELAY: 750, // Delay between each tension symbol stop in cascading (milliseconds)
      TURBO_DURATION: 800, // Shorter tension duration for turbo mode (milliseconds)
      TURBO_CASCADE_DELAY: 150, // Faster cascade delay for turbo tension mode (milliseconds)
    },
    // Turbo mode configuration
    TURBO: {
      SPIN_DURATION: 200, // Minimal spin duration in turbo - drop almost immediately after server response (milliseconds)
      DROP_DURATION: 500, // Drop animation duration - matches regular stop feel (milliseconds)
      BOUNCE_OVERSHOOT: 1.0, // No bounce - lands exactly in place (1.0 = no overshoot)
      SYMBOL_STOP_CASCADE_DELAY: 65, // Cascade delay between symbols in turbo mode (milliseconds)
    },
    // Collection phase configuration
    COLLECTION: {
      TRIGGER_DELAY: 1000, // Delay before starting collection after conditions are met (milliseconds)
      INTRO_ANIMATION_DELAY: 750, // Wait time for collector intro animation (milliseconds)
      SYMBOL_COLLECT_DELAY: 750, // Delay between collecting each symbol (milliseconds)
      MULTIPLIER_UPDATE_DELAY: 50, // Delay after multiplier animation completes to show the multiplied value before fading (milliseconds)
      FINISH_DELAY: 500, // Delay after all symbols collected before finishing (milliseconds)
      OUTRO_DELAY: 1000, // Delay before triggering outro after collection finishes (milliseconds)
      // Counter animation durations
      COUNTER_ANIMATION_PER_COIN: 300, // Counter animation duration per coin in normal mode (milliseconds)
      COUNTER_ANIMATION_SKIP_MODE: 2000, // Counter animation duration in skip mode (milliseconds)
      COUNTER_ANIMATION_MULTIPLIER: 1000, // Counter animation duration for progress bar multiplier (milliseconds)
      ESTIMATED_TIME_PER_COIN: 1600, // Estimated time per coin for continuous counter animation (milliseconds)
    },
  },
  // Win threshold multipliers - loaded from provider-specific configuration
  WIN_THRESHOLDS: PROVIDER_CONFIG.WIN_THRESHOLDS,
  // Win celebration animation configuration
  WIN_CELEBRATION: {
    AUTO_CLOSE_DELAY: 3000, // Auto-close delay after counter finishes (milliseconds) - popup closes automatically if user doesn't interact
  },
  // Max win dialog configuration
  MAX_WIN: {
    ENABLED: true, // When true, max_win animation plays as final tier in win sequence
    AUTO_CLOSE_DELAY: 30000, // Auto-close delay after counter finishes (30 seconds) - dialog closes automatically if user doesn't interact
    MULTIPLIER:
      PROVIDER_CONFIG.WIN_THRESHOLDS.MAX_WIN_THRESHOLD ||
      PROVIDER_CONFIG.MAX_WIN_MULTIPLIER ||
      5000, // Max win cap multiplier (bet × this value) - loaded from provider config
  },
  // Hold and win outro dialog configuration
  HOLD_AND_WIN_OUTRO: {
    AUTO_CLOSE_DELAY: 30000, // Auto-close delay after counter finishes (30 seconds) - dialog closes automatically if user doesn't interact
  },
  // Emergency landing pattern for API rejections (e.g., error 205 free rounds available)
  // Pattern is now generated dynamically via generateNonWinningCombination() utility
  EMERGENCY_LANDING: {
    // Fallback pattern used if dynamic generation fails after max attempts
    // Guaranteed non-winning combination: no coins, no wilds, all different symbols
    // Dynamically generated fallback pattern — guaranteed non-winning combination for any reel count
    FALLBACK_PATTERN: Array.from(
      { length: Settings.REEL_COUNT },
      (_, reelIdx) =>
        Array.from(
          { length: GAME_LAYOUT.SYMBOLS_VISIBLE },
          (_, symIdx) => (reelIdx + symIdx + 4) % 10 || 4,
        ),
    ),
  },
};

// Win type string constants - loaded from provider-specific configuration
export const WIN_TYPES = PROVIDER_CONFIG.WIN_TYPES;

// Animated win types - which wins trigger full-screen celebration
export const ANIMATED_WIN_TYPES = PROVIDER_CONFIG.ANIMATED_WIN_TYPES;

// Centralized mapping of win types to their thresholds (dynamically generated)
export const WIN_TYPE_THRESHOLDS = Object.keys(
  PROVIDER_CONFIG.WIN_TYPES,
).reduce((acc, key) => {
  const thresholdKey = `${key}_THRESHOLD`;
  if (PROVIDER_CONFIG.WIN_THRESHOLDS[thresholdKey]) {
    acc[PROVIDER_CONFIG.WIN_TYPES[key]] =
      PROVIDER_CONFIG.WIN_THRESHOLDS[thresholdKey];
  }
  return acc;
}, {});

// Expanding wild functions - cascading wild animations
/**
 * Build cascade sequences dynamically based on Settings.SYMBOLS_VISIBLE
 * Returns sequences for each possible wild position
 */
export const buildCascadeSequences = () => {
  const visibleCount = GAME_LAYOUT.SYMBOLS_VISIBLE;
  const sequences = {};

  for (let wildPos = 0; wildPos < visibleCount; wildPos++) {
    const sequence = [wildPos]; // Start with wild position

    if (wildPos === 0) {
      // Wild at top → cascade down
      for (let i = 1; i < visibleCount; i++) {
        sequence.push(i);
      }
    } else if (wildPos === visibleCount - 1) {
      // Wild at bottom → cascade up
      for (let i = visibleCount - 2; i >= 0; i--) {
        sequence.push(i);
      }
    } else {
      // Wild in middle → fill one direction first, then the other
      const isLowerHalf = wildPos >= Math.floor(visibleCount / 2);

      if (isLowerHalf) {
        // Fill below first, then above
        for (let i = wildPos + 1; i < visibleCount; i++) {
          sequence.push(i);
        }
        for (let i = wildPos - 1; i >= 0; i--) {
          sequence.push(i);
        }
      } else {
        // Fill above first, then below
        for (let i = wildPos - 1; i >= 0; i--) {
          sequence.push(i);
        }
        for (let i = wildPos + 1; i < visibleCount; i++) {
          sequence.push(i);
        }
      }
    }

    sequences[wildPos] = sequence;
  }

  return sequences;
};

export const EXPANDING_WILD_SEQUENCES = buildCascadeSequences();

/**
 * Detect which middle reels (1, 2, 3) have wild symbols when there's a win
 * Wild should expand if there's a connected winning line adjacent to it
 * @param {Object} currentRoundRef - Ref containing current round data
 * @param {Array} wins - Array of winning combinations from currentRound.wins
 * @returns {Array<{reelIndex: number, wildPosition: number}>} Array of objects with reel index and wild position
 */
export const detectExpandingWilds = (currentRoundRef, wins) => {
  const expandedReels = currentRoundRef.current?.reels;

  if (!expandedReels || !wins || !Array.isArray(wins) || wins.length === 0) {
    return [];
  }

  // Use wild symbol ID from settings (calculated once at module load)
  if (WILD_SYMBOL_ID === null) {
    return [];
  }

  // Dynamic middle reel indices — all reels except first and last
  const middleReelIndices = Array.from(
    { length: Math.max(0, Settings.REEL_COUNT - 2) },
    (_, i) => i + 1,
  );
  const reelsWithWilds = [];

  for (const reelIndex of middleReelIndices) {
    const visibleSymbols = expandedReels[reelIndex];
    if (!visibleSymbols || !Array.isArray(visibleSymbols)) {
      continue;
    }

    // Find FIRST wild position in visible symbols (searching for w1 by symbol ID)
    const wildPosition = visibleSymbols.findIndex(
      (symbolId) => symbolId === WILD_SYMBOL_ID,
    );

    // If wild found, check if it's part of a connected winning line
    if (wildPosition !== -1) {
      // Check if ANY winning combination has this wild reel as part of a connected line
      const isPartOfConnectedWin = wins.some((win) => {
        if (!Array.isArray(win.positions) || win.positions.length === 0) {
          return false;
        }

        // Get all reels involved in this win
        const reelsInWin = win.positions.map(([r]) => r);
        const uniqueReels = [...new Set(reelsInWin)].sort((a, b) => a - b);

        // Check if wild reel is in this win
        if (!uniqueReels.includes(reelIndex)) {
          return false;
        }

        // Find the rightmost connected reel (consecutive from left)
        let rightmostConnected = -1;
        for (let i = 0; i < uniqueReels.length; i++) {
          const currentReel = uniqueReels[i];

          // First reel must be 0 or 1 (wins start from left)
          if (i === 0 && currentReel > 1) {
            break;
          }

          // Check if this reel is consecutive to the previous one
          if (i > 0 && currentReel !== uniqueReels[i - 1] + 1) {
            break; // Gap found, stop here
          }

          rightmostConnected = currentReel;
        }

        // Wild should expand if it's part of the connected sequence (not after a gap)
        const isInConnectedSequence = rightmostConnected >= reelIndex;

        return isInConnectedSequence;
      });

      // Expand wild if it's part of a connected winning line
      if (isPartOfConnectedWin) {
        reelsWithWilds.push({
          reelIndex,
          wildPosition, // This is overlay position (0-3)
        });
      }
    }
  }

  return reelsWithWilds;
};

/**
 * Get the appear animation duration for expanding wilds
 * @param {Object} overlayReelController - Controller to get animation duration
 * @returns {number} Appear animation duration in milliseconds (default: 1000ms)
 */
export const getAppearAnimationDuration = (overlayReelController) => {
  // Default fallback: 1000ms
  let appearAnimationDuration = 1000;

  if (overlayReelController) {
    // Use wild symbol ID from settings (calculated once at module load)
    if (WILD_SYMBOL_ID === null) {
      return appearAnimationDuration;
    }

    // Try to get duration from a spine instance
    const spineKey = `0_${WILD_SYMBOL_ID}`; // First position, w1 wild symbol
    const spineInstance =
      overlayReelController.prePositionedSpines?.get(spineKey);

    if (spineInstance?.skeleton?.data?.animations) {
      // Try "appear" first, then "land" as fallback
      let targetAnimation = spineInstance.skeleton.data.animations.find(
        (anim) => anim.name.toLowerCase().includes("appear"),
      );
      if (!targetAnimation) {
        targetAnimation = spineInstance.skeleton.data.animations.find((anim) =>
          anim.name.toLowerCase().includes("land"),
        );
      }
      if (targetAnimation?.duration) {
        appearAnimationDuration = Math.round(targetAnimation.duration * 1000);
      }
    }
  }

  return appearAnimationDuration;
};

/**
 * Calculate the total duration for cascading expanding wild animation
 * @param {number} wildPosition - Position where wild was detected (0-3)
 * @param {Object} overlayReelController - Controller to get animation duration
 * @returns {number} Total duration in milliseconds
 */
export const calculateCascadeDuration = (
  wildPosition,
  overlayReelController,
) => {
  // Get the sequence for this wild position
  const sequence =
    EXPANDING_WILD_SEQUENCES[wildPosition] ||
    Array.from({ length: GAME_LAYOUT.SYMBOLS_VISIBLE }, (_, i) => i);

  // Get appear animation duration using helper function
  const appearAnimationDuration = getAppearAnimationDuration(
    overlayReelController,
  );

  // Calculate total cascade time (event-aware timing):
  // - Dynamic delays between symbols: (sequence.length - 1) * (appearDuration * overlap%)
  // - Plus appear animation duration for the last symbol
  const cascadeDelay = Math.round(
    appearAnimationDuration *
    FEATURES_CONFIG.EXPANDING_WILDS.CASCADE_OVERLAP_PERCENTAGE,
  );
  const cascadeDelayTime = (sequence.length - 1) * cascadeDelay;
  const totalDuration = cascadeDelayTime + appearAnimationDuration;

  return totalDuration;
};

/**
 * Play cascading expanding wild animation for a single reel
 * @param {number} reelIndex - Index of the reel to animate
 * @param {number} wildPosition - Position where wild was detected (0-3)
 * @param {Function} dispatch - Redux dispatch function to update state
 */
export const playCascadingExpandingWild = async (
  reelIndex,
  wildPosition,
  reelRefs,
  overlayReelRefs,
  currentTimeoutsRef,
  dispatch,
) => {
  const reels = reelRefs.current;
  const overlayReels = overlayReelRefs.current;

  if (!reels || !overlayReels) {
    return;
  }

  const reelRef = reels[reelIndex];
  const overlayReelRef = overlayReels[reelIndex];

  if (!reelRef || !overlayReelRef) {
    return;
  }

  const reelController = reelRef?.getReelController?.();
  const overlayReelController = overlayReelRef?.getOverlayReelController?.();

  if (!reelController || !overlayReelController) {
    return;
  }

  // Use wild symbol ID from settings (calculated once at module load)
  if (WILD_SYMBOL_ID === null) {
    return;
  }

  // Get the sequence for this wild position
  const sequence =
    EXPANDING_WILD_SEQUENCES[wildPosition] ||
    Array.from({ length: GAME_LAYOUT.SYMBOLS_VISIBLE }, (_, i) => i);

  // Track all symbol replacements for Redux update
  const symbolReplacements = [];

  // Track the last spine instance for event listening
  let lastSpineInstance = null;

  // Calculate cascade delay dynamically based on animation duration (event-aware timing)
  const appearAnimationDuration = getAppearAnimationDuration(
    overlayReelController,
  );
  const cascadeDelay = Math.round(
    appearAnimationDuration *
    FEATURES_CONFIG.EXPANDING_WILDS.CASCADE_OVERLAP_PERCENTAGE,
  );

  // Play animations in sequence with dynamic delays
  for (let i = 0; i < sequence.length; i++) {
    const symbolIndex = sequence[i];

    // Show overlay appear animation at this position (using w1 wild symbol)
    overlayReelController.showAppearAnimationAtPosition(
      symbolIndex,
      WILD_SYMBOL_ID,
    );

    // Update reel texture immediately (no RAF needed in async function)
    reelController.replaceSymbolAtPosition(symbolIndex, WILD_SYMBOL_ID);

    // Track this replacement for Redux update
    symbolReplacements.push({
      position: symbolIndex,
      symbolId: WILD_SYMBOL_ID,
    });

    // Get the spine instance for the last symbol (for event listening)
    if (i === sequence.length - 1) {
      const spineKey = `${symbolIndex}_${WILD_SYMBOL_ID}`;
      lastSpineInstance =
        overlayReelController.prePositionedSpines?.get(spineKey);
    }

    // Wait before next symbol (except for the last one) - event-aware timing
    // IMPORTANT: Do NOT push to currentTimeoutsRef — these are cascade-local delays.
    // Pushing to the shared ref causes reel N's timeout to be cleared when reel M
    // dispatches updateReelSymbols (triggering a re-render that clears the ref).
    if (i < sequence.length - 1) {
      await new Promise((resolve) => {
        const { default: TickerTimeout } = require("./utils/TickerTimeout");
        new TickerTimeout(resolve, cascadeDelay);
      });
    }
  }

  // Update Redux state with all wild replacements for this reel
  // This ensures next spin reads correct symbols from currentRound.reels
  if (dispatch && symbolReplacements.length > 0) {
    // Import the action dynamically to avoid circular dependency
    const { updateReelSymbols } = await import("./State/actions");
    dispatch(updateReelSymbols({ reelIndex, symbolReplacements }));
  }

  // Wait for the last symbol's animation to complete (event-driven)
  // Resolves on any non-looping animation complete — works for both "appear" and "land" fallback
  if (lastSpineInstance && lastSpineInstance.state) {
    return new Promise((resolve) => {
      const completeListener = {
        complete: () => {
          lastSpineInstance.state.removeListener(completeListener);
          resolve();
        },
      };
      lastSpineInstance.state.addListener(completeListener);
    });
  }
};

/**
 * Get the "in" animation duration for expanding wild full-reel animation
 * @param {Object} overlayReelController - Controller to get animation duration
 * @returns {number} "in" animation duration in milliseconds (default: 300ms)
 */
export const getExpandingWildInAnimationDuration = (overlayReelController) => {
  // Default fallback: 300ms (typical "in" animation duration)
  let inAnimationDuration = 300;

  if (overlayReelController?.expandingWildSpine?.skeleton?.data?.animations) {
    const inAnimation =
      overlayReelController.expandingWildSpine.skeleton.data.animations.find(
        (anim) => anim.name === "in",
      );
    if (inAnimation?.duration) {
      inAnimationDuration = Math.round(inAnimation.duration * 1000);
    }
  }

  return inAnimationDuration;
};

/**
 * Play full-reel expanding wild animation for a single reel
 * @param {number} reelIndex - Index of the reel to animate
 * @param {Object} reelRefs - Refs to reel components (for texture replacement)
 * @param {Object} overlayReelRefs - Refs to overlay reel components
 * @returns {Promise} Resolves when "appear" animation completes (event-driven)
 */
export const playFullReelExpandingWild = async (
  reelIndex,
  reelRefs,
  overlayReelRefs,
) => {
  const reels = reelRefs.current;
  const overlayReels = overlayReelRefs.current;

  if (!overlayReels || !reels) {
    return;
  }

  const reelRef = reels[reelIndex];
  const overlayReelRef = overlayReels[reelIndex];

  if (!overlayReelRef || !reelRef) {
    return;
  }

  // Get controllers
  const overlayReelController = overlayReelRef?.getOverlayReelController?.();

  if (!overlayReelController) {
    return;
  }

  // Play the full-reel animation (appear → idle)
  // This will initialize the spine if needed
  overlayReelRef.playExpandingWildFullReel?.();

  // Get the expanding wild spine instance
  const expandingWildSpine = overlayReelController.getExpandingWildSpine?.();

  if (!expandingWildSpine || !expandingWildSpine.state) {
    return;
  }

  // Wait for "appear" animation to complete (event-driven)
  return new Promise((resolve) => {
    const completeListener = {
      complete: (entry) => {
        if (entry.animation.name === "appear") {
          expandingWildSpine.state.removeListener(completeListener);
          resolve();
        }
      },
    };
    expandingWildSpine.state.addListener(completeListener);
  });
};
